import { useEffect, useRef, useState } from "react";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import ClearIcon from '@mui/icons-material/Clear';
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../assests/form_guru_logo.png";
import { deactivateDeviceToken } from "../services/notification";
import NotificationPopover from "./NotificationPopover";

function readStoredUser() {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) {
    return null;
  }

  try {
    return JSON.parse(storedUser);
  } catch {
    return null;
  }
}

const PREDICTION_TABS = [
  { id: "predictions", label: "Predictions" },
  { id: "performance", label: "Model Performance", adminOnly: true },
  { id: "admin", label: "Admin Panel", adminOnly: true },
];

export default function Navbar({
  activePredictionTab = null,
  onPredictionTabChange = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const profileMenuRef = useRef(null);
  const profileMenuMobileRef = useRef(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [user, setUser] = useState(readStoredUser());
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isAuthenticated = !!user && !!localStorage.getItem("access_token");
  const isAdmin = user && (user.is_superuser || user.is_staff);
  const isPredictionsPage = location.pathname === "/predictions";
  const visiblePredictionTabs = PREDICTION_TABS.filter(
    (tab) => !tab.adminOnly || isAdmin
  );
  const highlightedPredictionTab = visiblePredictionTabs.some(
    (tab) => tab.id === activePredictionTab
  )
    ? activePredictionTab
    : null;

  let profileInitial = "U";
  if (user) {
    const displayName =
      user.first_name || user.firstName || user.name || user.email || "";
    profileInitial = displayName.trim().charAt(0).toUpperCase() || "U";
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      const insideDesktop =
        profileMenuRef.current &&
        profileMenuRef.current.contains(event.target);
      const insideMobile =
        profileMenuMobileRef.current &&
        profileMenuMobileRef.current.contains(event.target);
      if (!insideDesktop && !insideMobile) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const syncUser = () => {
      setUser(readStoredUser());
    };

    window.addEventListener("storage", syncUser);
    window.addEventListener("user-profile-updated", syncUser);

    return () => {
      window.removeEventListener("storage", syncUser);
      window.removeEventListener("user-profile-updated", syncUser);
    };
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isMenuOpen]);

  const handleLogout = async () => {
    try {
      await deactivateDeviceToken();
    } catch (e) {
      // Ignore deactivation failures and proceed with logout
    }
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("userEmail");
    setUser(null);
    setIsProfileMenuOpen(false);
    window.dispatchEvent(new Event("user-profile-updated"));
    navigate("/");
  };

  const handleProfile = () => {
    setIsProfileMenuOpen(false);
    navigate("/profile");
  };

  const handlePredictionTabClick = (tabId) => {
    setIsMenuOpen(false);
    if (isPredictionsPage && onPredictionTabChange) {
      onPredictionTabChange(tabId);
      return;
    }

    navigate("/predictions", { state: { predictionTab: tabId } });
  };

  return (
    <nav className="flex justify-center items-center sticky top-0 z-50 bg-[var(--color-bg)] backdrop-blur-[12px] py-4 px-5 border-b border-[var(--color-border)]">
      <div className="max-w-[1280px] w-full flex items-center justify-between">
        <Link to="/" className="flex items-center md:gap-3 gap-2">
          <img
            src={logo}
            alt="Form Guru"
            className="xl:h-20 md:h-16 h-12 w-auto"
          />
          <span className="brand-wordmark xl:text-[28px] md:text-2xl text-base text-[var(--color-text)]">
            FORM<span className="text-[var(--color-primary)]"> GURU</span>
          </span>
        </Link>

        <div className="md:flex hidden items-center gap-6">
          <div className="flex items-center flex-wrap gap-4">
            {visiblePredictionTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handlePredictionTabClick(tab.id)}
                className={`bg-transparent border-b-2 cursor-pointer text-sm font-bold p-0 uppercase whitespace-nowrap hover:text-[var(--color-primary)] ${highlightedPredictionTab === tab.id ? "border-[var(--color-primary)] text-[var(--color-primary)]" : "border-transparent text-[var(--color-text-muted)]"}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <NotificationPopover isAuthenticated={isAuthenticated} />

          <div ref={profileMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setIsProfileMenuOpen((open) => !open)}
              aria-label="Open profile menu"
              aria-expanded={isProfileMenuOpen}
              className="bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-primary)] h-[38px] w-[38px] rounded-full cursor-pointer inline-flex items-center justify-center text-base font-extrabold leading-none"
            >
              {profileInitial}
            </button>

            {isProfileMenuOpen && (
              <div className="absolute right-0 top-[calc(100%+12px)] min-w-[160px] bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-1.5 z-[100]">
                <button
                  type="button"
                  onClick={handleProfile}
                  className="w-full bg-transparent border-0 text-[var(--color-text)] py-2.5 px-3 rounded-[var(--radius-md)] cursor-pointer flex items-center gap-2.5 text-xs font-bold uppercase text-left"
                >
                  <SettingsIcon fontSize="small" />
                  Profile
                </button>

                {isAuthenticated && (
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full bg-transparent border-0 text-[var(--color-accent-red)] py-2.5 px-3 rounded-[var(--radius-md)] cursor-pointer flex items-center gap-2.5 text-xs font-bold uppercase text-left"
                  >
                    <LogoutIcon fontSize="small" />
                    Logout
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="md:hidden flex items-center gap-3">
          <NotificationPopover isAuthenticated={isAuthenticated} />
          <div ref={profileMenuMobileRef} className="relative">
            <button
              type="button"
              onClick={() => setIsProfileMenuOpen((open) => !open)}
              aria-label="Open profile menu"
              aria-expanded={isProfileMenuOpen}
              className="bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-primary)] h-[38px] w-[38px] rounded-full cursor-pointer inline-flex items-center justify-center text-base font-extrabold leading-none"
            >
              {profileInitial}
            </button>

            {isProfileMenuOpen && (
              <div className="absolute right-0 top-[calc(100%+8px)] min-w-[160px] bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-1.5 z-[200]">
                <button
                  type="button"
                  onClick={handleProfile}
                  className="w-full bg-transparent border-0 text-[var(--color-text)] py-2.5 px-3 rounded-[var(--radius-md)] cursor-pointer flex items-center gap-2.5 text-xs font-bold uppercase text-left"
                >
                  <SettingsIcon fontSize="small" />
                  Profile
                </button>

                {isAuthenticated && (
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full bg-transparent border-0 text-[var(--color-accent-red)] py-2.5 px-3 rounded-[var(--radius-md)] cursor-pointer flex items-center gap-2.5 text-xs font-bold uppercase text-left"
                  >
                    <LogoutIcon fontSize="small" />
                    Logout
                  </button>
                )}
              </div>
            )}
          </div>
          {isMenuOpen ? (
            <ClearIcon fontSize="small" sx={{ color: 'var(--color-primary)' }} onClick={() => setIsMenuOpen(false)} />
          ) : (
            <DensityMediumIcon fontSize="small" sx={{ color: 'var(--color-primary)' }} onClick={() => setIsMenuOpen(true)} />
          )}
        </div>

        {isMenuOpen && (
          <div className="absolute right-0 top-[81px] min-w-full min-h-[calc(100vh-81px)] bg-[var(--color-bg)] py-4 px-5 z-[100]">
            <div className="flex flex-col justify-center items-center flex-wrap gap-4">
              {visiblePredictionTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handlePredictionTabClick(tab.id)}
                  className={`bg-transparent border-b-2 cursor-pointer text-base font-bold p-0 uppercase whitespace-nowrap hover:text-[var(--color-primary)] ${highlightedPredictionTab === tab.id ? "border-[var(--color-primary)] text-[var(--color-primary)]" : "border-transparent text-[var(--color-text-muted)]"}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
